'use client';

import React from 'react';
import { ExternalLink } from 'lucide-react';

function InstagramIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function YoutubeIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
      <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" />
    </svg>
  );
}

function FacebookIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

const SOCIAL_LINKS = [
  {
    name: 'Instagram',
    handle: '@rumbaexplosions',
    icon: InstagramIcon,
    href: 'https://www.instagram.com/rumbaexplosions/',
    color: '#e1306c',
    bg: 'rgba(225,48,108,0.1)',
    border: 'rgba(225,48,108,0.25)',
  },
  {
    name: 'YouTube',
    handle: 'Rumbaexplosions',
    icon: YoutubeIcon,
    href: 'https://www.youtube.com/@rumbaexplosions',
    color: '#ff0000',
    bg: 'rgba(255,0,0,0.1)',
    border: 'rgba(255,0,0,0.25)',
  },
  {
    name: 'Facebook',
    handle: 'Rumbaexplosions',
    icon: FacebookIcon,
    href: 'https://www.facebook.com/rumbaexplosions',
    color: '#1877f2',
    bg: 'rgba(24,119,242,0.1)',
    border: 'rgba(24,119,242,0.25)',
  },
];

export default function RumbaSocialSection() {
  return (
    <section className="px-6 md:px-12 py-16 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 100%, rgba(124,58,237,0.08) 0%, transparent 60%)',
        }}
      />

      <div className="relative max-w-4xl mx-auto text-center">
        <p
          className="text-xs font-bold uppercase tracking-widest mb-3"
          style={{ color: '#fb923c' }}
        >
          Síguenos
        </p>
        <h2 className="text-3xl md:text-4xl font-black text-foreground mb-4" style={{ letterSpacing: '-0.02em' }}>
          Únete a la comunidad
        </h2>
        <p className="text-muted-foreground max-w-md mx-auto mb-10 text-base">
          Sigue a Rumbaexplosions en redes sociales y sé parte de la familia que hace vibrar a Medellín.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {SOCIAL_LINKS?.map((social) => {
            const SocialIcon = social?.icon;
            return (
              <a
                key={social?.name}
                href={social?.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-6 py-4 rounded-2xl transition-all duration-200 hover:scale-105 active:scale-95 w-full sm:w-auto"
                style={{
                  background: social?.bg,
                  border: `1px solid ${social?.border}`,
                  minWidth: '200px',
                }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${social?.color}20`, border: `1px solid ${social?.color}30` }}
                >
                  <SocialIcon size={20} style={{ color: social?.color }} />
                </div>
                <div className="text-left flex-1">
                  <p className="text-sm font-bold text-foreground">{social?.name}</p>
                  <p className="text-xs text-muted-foreground">{social?.handle}</p>
                </div>
                <ExternalLink size={14} className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
              </a>
            );
          })}
        </div>

        {/* Website link */}
        <div className="mt-8 flex items-center justify-center gap-2">
          <span className="text-xs text-muted-foreground">También en:</span>
          <a
            href="https://rumbaexplosion.co"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold hover:underline transition-colors"
            style={{ color: '#fb923c' }}
          >
            rumbaexplosion.co
          </a>
        </div>
      </div>
    </section>
  );
}
